import PropTypes from 'prop-types';
import { useContext, createContext, useMemo, useCallback, useEffect } from 'react';
import { repsSelector, requestRepsAsync, salesSettingsByRepSelector } from '@/modules/AdminTools/redux/planBuilder/can-edit-settings/sales-settings';
import { useDispatch, useSelector } from 'react-redux';
import { planBuilderConstants } from '@/modules/AdminTools/lib/constants';

const { TARGET_ID_VALUE } = planBuilderConstants;

const RepContext = createContext();

const useGetRepOptions = () => useContext(RepContext);

const RepsProvider = ({ children }) => {
  const dispatch = useDispatch();

  const reps = useSelector(repsSelector);
  const salesSettingsByRep = useSelector(salesSettingsByRepSelector);

  useEffect(
    () => {
      if (salesSettingsByRep.length > 0){
        dispatch(requestRepsAsync.request({
          page: {
            number: 1,
            size: salesSettingsByRep.length,
          },
          filter: {
            ids: [...salesSettingsByRep.map(({ [TARGET_ID_VALUE]:target_id }) => target_id)],
          },
        }));
      }
    },
    [salesSettingsByRep, dispatch],
  );

  const options = useMemo(() => {
    if (reps) {
      const repIds = salesSettingsByRep.map(({ [TARGET_ID_VALUE]:target_id }) => target_id);

      return reps
        .filter(({ id }) => !repIds.includes(id))
        .map(({ id, name }) => ({
          label: name,
          value: id,
        }));
    }

    return [];
  }, [reps, salesSettingsByRep]);

  const getOptions = useCallback(
    (id) => {
      const matchingRep = reps.find((rep) => rep.id === id);
      const baseOptions = [];

      if (matchingRep) {
        baseOptions.push({
          label: matchingRep.name,
          value: id,
        });
      }

      return [...baseOptions, ...options];
    },
    [reps, options],
  );

  return (
    <RepContext.Provider value={getOptions}>{children}</RepContext.Provider>
  );
};

RepsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { RepsProvider, useGetRepOptions };
